import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { chamadoMovimentacaoService } from '../../services/chamadoMovimentacaoService';
import { toast } from 'react-toastify';

interface NovaMovimentacaoModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  chamadoId: number;
}

const NovaMovimentacaoModal: React.FC<NovaMovimentacaoModalProps> = ({
  open,
  onClose,
  onSuccess,
  chamadoId,
}) => {
  const [informacoes, setInformacoes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setInformacoes('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!informacoes.trim()) {
      setError('Informações são obrigatórias');
      return;
    }

    if (informacoes.trim().length < 1) {
      setError('Informações devem ter pelo menos 1 caractere');
      return;
    }

    if (informacoes.trim().length > 5000) {
      setError('Informações devem ter no máximo 5000 caracteres');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await chamadoMovimentacaoService.create(chamadoId, {
        informacoes: informacoes.trim(),
      });

      if (response.success) {
        toast.success('Movimentação criada com sucesso!');
        handleClose();
        onSuccess?.();
      } else {
        setError(response.message || 'Erro ao criar movimentação');
        toast.error(response.message || 'Erro ao criar movimentação');
      }
    } catch (error: any) {
      console.error('Erro ao criar movimentação:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar movimentação';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AddIcon color="primary" />
            <Typography variant="h6" component="span">
              Nova Movimentação
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          required
          label="Informações"
          multiline
          rows={6}
          value={informacoes}
          onChange={(e) => {
            setInformacoes(e.target.value);
            setError(null);
          }}
          disabled={loading}
          helperText={`Mínimo 1 caractere, máximo 5000 (${informacoes.length}/5000)`}
          error={!!error || (informacoes.length > 0 && informacoes.length > 5000)}
          size="small"
          sx={{ mt: 1 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<CloseIcon />}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !informacoes.trim()}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NovaMovimentacaoModal;

